/**
 * AdminCourseFilters
 *
 * Horizontal filter toolbar for the admin Course page. Replaces the left-side
 * `FilterPanel` sidebar in admin view — search + filters live on one row,
 * with the publish-date range tucked into a popover and active selections
 * shown as removable chips below.
 *
 * The Status segmented control is more useful than the old "Only Published"
 * boolean: admins can now isolate drafts, which the toggle couldn't express.
 */

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  InputAdornment,
  Popover,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '../ui';
import { DateRangeIcon, SearchIcon, VisibilityIcon } from '../icons';
import { CourseType, Tag, TagType, UserRole } from '../../apis/api-client';
import { tagsClient } from '../../apis/client-instances';
import { RootState } from '../../types';
import { tagScopeForUser } from '../../utils/tagFilters';
import { isAccountOrSystemAdmin } from '../../utils/roleHelpers';
import { TagAutocomplete } from '../common';

interface AdminCourseFiltersProps {
  filters: {
    name?: string;
    tagIds?: number[];
    publishedDateFrom?: string;
    publishedDateTo?: string;
    workspaceId?: number;
    isDraft?: boolean;
    isAssigned?: boolean;
    /** Multi-select: zero/all-types means "no filter". One or more types
     *  narrows the catalog to those types. */
    courseType?: CourseType[];
  };
  onFilterChange: (filters: any) => void;
  onClearFilters: () => void;
}

// Map our 3-state Status segmented control to the filters.isDraft tri-state:
//   'all'        → undefined  (no constraint)
//   'published'  → false      (isDraft=false)
//   'draft'      → true       (isDraft=true)
const statusFromDraft = (isDraft: boolean | undefined): 'all' | 'published' | 'draft' => {
  if (isDraft === false) return 'published';
  if (isDraft === true) return 'draft';
  return 'all';
};

const draftFromStatus = (status: 'all' | 'published' | 'draft'): boolean | undefined => {
  if (status === 'published') return false;
  if (status === 'draft') return true;
  return undefined;
};

const AdminCourseFilters: React.FC<AdminCourseFiltersProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
}) => {
  const currentUser = useSelector((state: RootState) => state.profile.user);
  const isAccountAdmin = isAccountOrSystemAdmin(currentUser?.role);
  const canSeeTags = isAccountAdmin;
  // Workspace admins only see published content (enforced server-side
  // in the table's fetcher). Hide the All/Published/Draft toggle for
  // them — toggling it wouldn't change what they see, so it'd just
  // be a confusing dead control.
  const isWorkspaceAdmin = currentUser?.role === UserRole.WorkspaceAdmin;

  // Hydrate full Tag objects from the id-only filter state so we can render
  // readable chip labels and pre-populate the tag picker.
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);

  useEffect(() => {
    if (!canSeeTags) return;
    const ids = filters.tagIds ?? [];
    if (ids.length === 0) {
      setSelectedTags([]);
      return;
    }
    if (selectedTags.length === ids.length && selectedTags.every((t) => t.id != null && ids.includes(t.id!))) {
      return;
    }
    let cancelled = false;
    const scope = tagScopeForUser(currentUser?.role, currentUser?.workspaceId);
    tagsClient
      .searchTags(undefined, TagType.CourseTag, scope.workspaceId, scope.includeAccountWide ?? false, scope.accountWideOnly ?? false, ids, 1, ids.length)
      .then((res) => {
        if (!cancelled) setSelectedTags(res.items ?? []);
      })
      .catch(() => {
        if (!cancelled) setSelectedTags([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.tagIds, currentUser?.role, currentUser?.workspaceId, canSeeTags]);

  // Publish-date popover state
  const [dateAnchor, setDateAnchor] = useState<HTMLElement | null>(null);
  const openDate = (e: React.MouseEvent<HTMLElement>) => setDateAnchor(e.currentTarget);
  const closeDate = () => setDateAnchor(null);

  // Debounced search — local state for the input, push to filters.name after
  // 300ms of inactivity so we don't hit the API on every keystroke.
  const [searchInput, setSearchInput] = useState(filters.name ?? '');
  useEffect(() => {
    const t = setTimeout(() => {
      if ((filters.name ?? '') !== searchInput) {
        onFilterChange({ ...filters, name: searchInput || undefined });
      }
    }, 300);
    return () => clearTimeout(t);
    // `filters` must be a dep: the parent replaces the whole filter object,
    // so a timeout that fired with a stale `filters` would clobber a facet
    // (tag/status/date) the user changed during the 300ms window. The stable
    // `onFilterChange` is intentionally omitted (it always calls the same
    // setter; adding the unmemoized prop would reset the debounce each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, filters]);
  // Keep local input in sync if parent clears filters.
  useEffect(() => {
    if ((filters.name ?? '') !== searchInput && !document.activeElement?.matches('input[data-admin-search]')) {
      setSearchInput(filters.name ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.name]);

  // Convenience setters
  const setStatus = (_: any, next: 'all' | 'published' | 'draft' | null) => {
    // ToggleButtonGroup yields null if the user clicks the currently-active
    // option. Treat that as "All" so users always have a defined state.
    onFilterChange({ ...filters, isDraft: draftFromStatus(next ?? 'all') });
  };
  const setTags = (tags: Tag[]) => {
    setSelectedTags(tags);
    const ids = tags.map((t) => t.id!).filter(Boolean);
    onFilterChange({ ...filters, tagIds: ids.length > 0 ? ids : undefined });
  };
  const setDate = (field: 'publishedDateFrom' | 'publishedDateTo', value: string) => {
    onFilterChange({ ...filters, [field]: value || undefined });
  };
  const setCourseTypes = (next: CourseType[]) => {
    // Empty array → no filter. Mirrors the "all types" semantics of the
    // empty option the single-select had.
    onFilterChange({ ...filters, courseType: next.length > 0 ? next : undefined });
  };

  // Per-chip removal — keeps the user from having to reopen a picker just to
  // unset one filter.
  const removeFilter = (key: string) => {
    if (key === 'tagIds') setSelectedTags([]);
    onFilterChange({ ...filters, [key]: undefined });
  };

  const courseTypeLabel = (t: CourseType): string => {
    if (t === CourseType.Standard) return 'Course';
    if (t === CourseType.External) return 'Event';
    if (t === CourseType.LearningPath) return 'Learning Path';
    return '';
  };

  const TYPE_OPTIONS: CourseType[] = [CourseType.Standard, CourseType.External, CourseType.LearningPath];
  const selectedTypes = filters.courseType ?? [];

  const activeChips: Array<{ key: string; label: string }> = [];
  if (selectedTypes.length > 0) {
    activeChips.push({
      key: 'courseType',
      label: `Type: ${selectedTypes.map(courseTypeLabel).join(', ')}`,
    });
  }
  if (canSeeTags && filters.tagIds && filters.tagIds.length > 0) {
    activeChips.push({
      key: 'tagIds',
      label: `Tags: ${selectedTags.map((t) => t.name).join(', ')}`,
    });
  }
  if (filters.publishedDateFrom) {
    activeChips.push({ key: 'publishedDateFrom', label: `From: ${filters.publishedDateFrom}` });
  }
  if (filters.publishedDateTo) {
    activeChips.push({ key: 'publishedDateTo', label: `To: ${filters.publishedDateTo}` });
  }
  const status = statusFromDraft(filters.isDraft);
  const hasAnyFilter = activeChips.length > 0 || status !== 'all' || !!filters.name;
  const hasDateRange = !!(filters.publishedDateFrom || filters.publishedDateTo);

  return (
    <Box sx={{ mb: 2 }}>
      {/* Main toolbar — search + filters on one row. Wraps on narrow screens.
          Order is by frequency of use: Search and Type first, then Status,
          Tags. Publish date + Clear all sit at the right. */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search content..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          inputProps={{ 'data-admin-search': '' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: '1 1 240px', minWidth: 200, maxWidth: 360 }}
        />

        <Autocomplete<CourseType, true, false, false>
          multiple
          disableCloseOnSelect
          size="small"
          options={TYPE_OPTIONS}
          value={selectedTypes}
          onChange={(_e, next) => setCourseTypes(next)}
          getOptionLabel={courseTypeLabel}
          isOptionEqualToValue={(opt, val) => opt === val}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option}>
              <Checkbox checked={selected} size="small" sx={{ mr: 1, p: 0.5 }} />
              {courseTypeLabel(option)}
            </li>
          )}
          renderTags={(items, getTagProps) =>
            items.map((option, index) => (
              <Chip
                label={courseTypeLabel(option)}
                size="small"
                {...getTagProps({ index })}
                key={option}
              />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} placeholder={selectedTypes.length === 0 ? 'All types' : ''} />
          )}
          sx={{ minWidth: 180 }}
        />

        {!isWorkspaceAdmin && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={status}
            onChange={setStatus}
            aria-label="Status filter"
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="published">Published</ToggleButton>
            <ToggleButton value="draft">Draft</ToggleButton>
          </ToggleButtonGroup>
        )}

        {canSeeTags && (
          <TagAutocomplete
            multiple
            value={selectedTags}
            onChange={(v: any) => setTags((v as Tag[]) ?? [])}
            tagType={TagType.CourseTag}
            {...tagScopeForUser(currentUser?.role, currentUser?.workspaceId)}
            placeholder="Filter by tags"
            sx={{ minWidth: 200 }}
          />
        )}

        <Button
          variant="outlined"
          size="small"
          startIcon={<DateRangeIcon />}
          onClick={openDate}
          // Override color/border explicitly so the button reads as a neutral
          // filter control like the surrounding outlined inputs (grey border +
          // dark text), and switches to primary accent when a range is set.
          sx={{
            color: hasDateRange ? 'primary.main' : 'text.primary',
            borderColor: hasDateRange ? 'primary.main' : 'rgba(0, 0, 0, 0.23)',
            '&:hover': {
              borderColor: hasDateRange ? 'primary.main' : 'rgba(0, 0, 0, 0.5)',
              bgcolor: 'action.hover',
            },
          }}
        >
          Publish date
        </Button>

        <Box sx={{ flex: 1 }} />

        {hasAnyFilter && (
          <Button variant="text" color="primary" size="small" onClick={onClearFilters}>
            Clear all
          </Button>
        )}
      </Box>

      {/* Active filter chips — purely informational + removal. The Status
          control doesn't get a chip; its own segmented UI already shows the
          current selection. */}
      {activeChips.length > 0 && (
        <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {activeChips.map((c) => (
            <Chip
              key={c.key}
              label={c.label}
              size="small"
              color="primary"
              variant="outlined"
              onDelete={() => removeFilter(c.key)}
            />
          ))}
        </Box>
      )}

      {/* Publish-date popover: From / To date inputs. */}
      <Popover
        open={!!dateAnchor}
        anchorEl={dateAnchor}
        onClose={closeDate}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            size="small"
            type="date"
            label="From"
            value={filters.publishedDateFrom || ''}
            onChange={(e) => setDate('publishedDateFrom', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={filters.publishedDateTo || ''}
            onChange={(e) => setDate('publishedDateTo', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Popover>
    </Box>
  );
};

export default AdminCourseFilters;
